/**
 * Phase 2: working papers, the statutory database, the accounts builder and the validation centre.
 * Mirrors Contracts/Practice/Phase2 on the API.
 */

// ---------------------------------------------------------------- Working papers

export interface WorkingPaperRow {
  templateCode: string;
  scheduleType: number;
  name: string;
  statementArea: number;
  reportingCode: string | null;
  helpText: string | null;
  scheduleId: string | null;
  status: string;
  preparedBy: string | null;
  preparedAtUtc: string | null;
  reviewedBy: string | null;
  reviewedAtUtc: string | null;
  scheduleTotal: number;
  atbTotal: number;
  difference: number;
  isReconciled: boolean;
  lineCount: number;
  evidenceCount: number;
  /** The sign-off was given against earlier figures and must be repeated. */
  isStale: boolean;
  /**
   * APT338 follow-up (the client, 28 Oct 2026 10:36): the heading the list is organised under - Assets, Liabilities, Equity or Profit & Loss - already in the
   * client's order. Empty from an API that does not send it (the list is then one table).
   */
  group?: string;
  /** "Explain difference": an explanation of the difference is stored with the schedule. */
  hasExplanation?: boolean;
  /**
   * APT349: the natural side of the schedule's reporting codes (1 debit, -1 credit); null when they mix both sides, absent from an API that does not send it. The totals
   * stay stored (debits positive, credits negative); the screen shows them, and takes what the user types, in the natural presentation.
   */
  normalSign?: number | null;
}

export interface WorkingPapersScreen {
  accountingPeriodId: string;
  periodLabel: string;
  frameworkCode: string;
  dataVersion: number;
  periodStatus: number;
  isLocked: boolean;
  schedules: WorkingPaperRow[];
}

export interface WorkingPaperTemplateLine {
  lineType: string;
  narrative: string;
  isTotal: boolean;
  sortOrder: number;
}

export interface WorkingPaperLine {
  id: string | null;
  lineType: string;
  narrative: string | null;
  currentAmount: number;
  priorAmount: number;
  sortOrder: number;
}

export interface WorkingPaperEvidenceRow {
  id: string;
  evidenceType: string;
  title: string;
  detail: string | null;
  storageRef: string | null;
  amount: number | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  /** prod-p3: the attached file, when there is one; the bytes come from the download endpoint. */
  hasFile: boolean;
  fileName: string | null;
  fileContentType: string | null;
  fileSizeBytes: number | null;
  fileUploadedAtUtc: string | null;
  fileUploadedBy: string | null;
  /** APT338 follow-up 2: the journal this item supports (an explanation or a document attached to an adjustment journal), and its number; null for the schedule's own evidence. */
  journalId?: string | null;
  journalNo?: number | null;
}

/** prod-p3: what the evidence upload accepts; mirrored by the API and the database CHECK. */
export const EVIDENCE_FILE_MAX_BYTES = 10 * 1024 * 1024;
export const EVIDENCE_FILE_ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.csv,.xls,.xlsx,.doc,.docx';

export interface WorkingPaperReconciliation {
  scheduleTotal: number;
  atbTotal: number;
  difference: number;
  isReconciled: boolean;
}

/**
 * APT338 follow-up (the client, 28 Oct 2026 10:36): the two statuses the API works out when a schedule is read (never stored). "NoBalance" - "No Balance / No Work
 * Required": nobody has touched the schedule and the Source TB, the Journals, the Adjusted TB, the schedule and the difference are all zero; it never holds
 * anything up and moves back to an active status when a balance or an entry is introduced. "SystemGenerated": a Profit and Loss schedule nobody has touched,
 * populated from the nominals and the adjusted trial balance for the user to review and sign off - never Reviewed by being listed.
 */
export const WORKING_PAPER_NO_BALANCE = 'NoBalance';
export const WORKING_PAPER_SYSTEM_GENERATED = 'SystemGenerated';

/** A nominal mapped to a lead schedule's reporting codes. */
export interface WorkingPaperNominal {
  nominalAccountId: string;
  code: string;
  description: string;
}

/**
 * APT338: what "Create adjustment journal" needs from the working paper: an Adjustment dated the last day of the period, and the nominals mapped to the
 * schedule's reporting codes. Null when the period cannot take a journal (finalised, filed or padlocked).
 */
export interface WorkingPaperJournalContext {
  typeCode: string;
  /** yyyy-MM-dd. */
  journalDate: string;
  nominals: WorkingPaperNominal[];
}

export interface WorkingPaperDetail {
  accountingPeriodId: string;
  templateCode: string;
  scheduleType: number;
  name: string;
  statementArea: number;
  reportingCode: string | null;
  helpText: string | null;
  scheduleId: string | null;
  status: string;
  conclusion: string | null;
  preparedBy: string | null;
  preparedAtUtc: string | null;
  reviewedBy: string | null;
  reviewedAtUtc: string | null;
  dataVersion: number;
  isLocked: boolean;
  isStale: boolean;
  templateLines: WorkingPaperTemplateLine[];
  lines: WorkingPaperLine[];
  evidence: WorkingPaperEvidenceRow[];
  reconciliation: WorkingPaperReconciliation;
  eTag: string;
  /** APT349: the natural side of the schedule's reporting codes (1 debit, -1 credit); null when they mix both sides. The rows and totals stay stored. */
  normalSign?: number | null;
  /**
   * APT338: the rows an untouched schedule opens with - the adjusted trial balance's own figures per nominal (Profit and Loss schedules, and balance sheet
   * schedules other than Fixed assets, Loans and Taxation). Suggestions only: nothing is stored until the user saves. Absent or empty otherwise.
   */
  suggestedLines?: WorkingPaperLine[] | null;
  journalContext?: WorkingPaperJournalContext | null;
  /** APT338 follow-up ("Explain difference"): the explanation stored with the schedule, and when it was given. It never allows sign-off. */
  differenceExplanation?: string | null;
  differenceExplainedAtUtc?: string | null;
  /** Where `suggestedLines` come from: the adjusted trial balance, or (Share capital) the Share Capital section's register. */
  suggestedSource?: WorkingPaperSuggestionSource | null;
  /** APT338 follow-up 2 (Share capital only): the full nominal value of the shares, the amount paid up and the amount unpaid. Absent for every other schedule. */
  shareCapital?: WorkingPaperShareCapital | null;
}

/** APT338 follow-up 2: Share capital keeps the full nominal value and identifies what is paid up and what is unpaid, this period and the year before (positive amounts). */
export interface WorkingPaperShareCapital {
  nominalValue: number;
  amountPaidUp: number;
  amountUnpaid: number;
  priorNominalValue: number;
  priorAmountPaidUp: number;
  priorAmountUnpaid: number;
  /** False when a class is partly paid but the amount paid on each share is not recorded in the Share Capital section, so the unpaid amount is not known. */
  unpaidRecorded: boolean;
}

export type WorkingPaperSuggestionSource = 'AdjustedTrialBalance' | 'ShareCapitalRegister';

export interface SaveWorkingPaperPayload {
  accountingPeriodId: string;
  scheduleType: number;
  templateCode: string | null;
  conclusion: string | null;
  /** APT338 follow-up: text stores the explanation of a difference, an empty string clears it, left out keeps what is stored. */
  differenceExplanation?: string;
  lines: Array<{
    lineType: string;
    narrative: string | null;
    currentAmount: number;
    priorAmount: number;
    sortOrder: number;
  }>;
}

export type WorkingPaperSignOffAction = 'Prepare' | 'Review' | 'ClearPrepare' | 'ClearReview';

export interface WorkingPaperSignOffPayload {
  accountingPeriodId: string;
  scheduleType: number;
  action: WorkingPaperSignOffAction;
}

export interface SaveWorkingPaperEvidencePayload {
  accountingPeriodId: string;
  scheduleType: number;
  id: string | null;
  evidenceType: string;
  title: string;
  detail: string | null;
  storageRef: string | null;
  amount: number | null;
  /** APT338 follow-up 2: the journal this item supports; left out for the schedule's own evidence. */
  journalId?: string | null;
}

// ---------------------------------------------------------------- Statutory database

/** 0 Not applicable, 1 Optional, 2 Required, 3 Overridden, 4 Error. */
export const STATUTORY_STATES = [
  { id: -1, name: 'All states' },
  { id: 2, name: 'Required' },
  { id: 1, name: 'Optional' },
  { id: 0, name: 'Not applicable' },
  { id: 3, name: 'Overridden' },
  { id: 4, name: 'Error' },
];

export interface StatutoryOverrideInfo {
  state: number;
  stateName: string;
  reason: string;
  overriddenBy: string;
  overriddenAtUtc: string;
}

export interface StatutoryFieldRow {
  fieldId: string;
  fieldCode: string;
  sectionCode: string;
  sectionTitle: string;
  label: string;
  helpText: string | null;
  valueType: number;
  /** The state to display; 3 Overridden and 4 Error collapse the state actually in force. */
  state: number;
  stateName: string;
  /** prod-p3: the state in force after any override (0 Not applicable, 1 Optional, 2 Required). */
  effectiveState: number;
  effectiveStateName: string;
  ruleCode: string | null;
  whyRequired: string | null;
  sourceReference: string | null;
  trigger: string | null;
  fixRoute: string | null;
  allowOverride: boolean;
  hasValue: boolean;
  /** The answer as shown: what `answerSource` says when set (it is what prints), else the statutory database's own. */
  answer: string | null;
  answeredAtUtc: string | null;
  override: StatutoryOverrideInfo | null;
  /**
   * APT250: the Settings screen that answers the field (e.g. "Company Information"); the accounts print its answer, so
   * `answer` is that screen's (null when it holds a note) and it is changed there, not here. Null/absent otherwise.
   */
  answerSource?: string | null;
  /** APT250: the route of the `answerSource` screen. */
  answerSourceRoute?: string | null;
  /** APT250: set when an answer of the field's own, which the accounts do not print, sits beside the Settings one. */
  answerNote?: string | null;
}

export interface StatutorySection {
  sectionCode: string;
  title: string;
  requiredCount: number;
  errorCount: number;
  fields: StatutoryFieldRow[];
}

export interface StatutoryStateCounts {
  required: number;
  optional: number;
  notApplicable: number;
  overridden: number;
  error: number;
}

export interface StatutoryDatabaseScreen {
  accountingPeriodId: string;
  periodLabel: string;
  frameworkCode: string;
  accountsTypeCode: string;
  regulatoryPackVersionId: string;
  dataVersion: number;
  isLocked: boolean;
  counts: StatutoryStateCounts;
  sections: StatutorySection[];
  /** APT250: why the disclosures are read-only when `isLocked` is set. */
  lockedReason?: string | null;
  /**
   * APT250: a state can be overridden or an override removed - false only for a finalised or filed period. Absent from
   * an older API, when `!isLocked` stands in for it.
   */
  canOverride?: boolean;
}

export interface OverrideDisclosurePayload {
  accountingPeriodId: string;
  fieldCode: string;
  /** Null clears the override. */
  state: number | null;
  reason: string | null;
}

// ---------------------------------------------------------------- Accounts builder

/** 0 Required, 1 Optional, 2 Omission permitted, 3 Not permitted. */
export interface OutputProfilePageRow {
  pageId: number;
  pageName: string;
  inclusion: number;
  inclusionName: string;
  include: boolean;
  canChange: boolean;
  ruleCode: string | null;
  reason: string | null;
  sourceReference: string | null;
  /**
   * item 137: where the tick came from, highest first - a rule that fixes the page, the choice saved for this period,
   * the client's Report Settings, or the shipped default. Absent from an API older than item 137.
   */
  includeSource?: 'Rule' | 'SavedForThisPeriod' | 'ReportSettings' | 'SystemDefault';
  /** The same, worded for the screen: "Rule", "Saved for this period", "Report Settings", "System default". */
  includeSourceName?: string;
}

export interface OutputProfileBlocker {
  ruleCode: string;
  message: string;
  sourceReference: string | null;
  fixRoute: string | null;
}

export interface OutputProfileRow {
  profileCode: string;
  name: string;
  description: string | null;
  pageVariant: number;
  isFilingCopy: boolean;
  isNonStatutory: boolean;
  isEnabled: boolean;
  isEligible: boolean;
  isStale: boolean;
  generatedAtUtc: string | null;
  pages: OutputProfilePageRow[];
  blockers: OutputProfileBlocker[];
  eTag: string;
  /**
   * APT251: the Report Type this output produces (the report's accountsVariant / ReportRow.typeId: 0 Full, 1 Abridged,
   * 2 Filleted), so the builder opens on the selected report's output; null/absent for HMRC, Management, Detailed P&L.
   */
  reportVariant?: number | null;
}

export interface AccountsBuilderScreen {
  accountingPeriodId: string;
  clientId: string;
  periodLabel: string;
  frameworkCode: string;
  accountsTypeCode: string;
  dataVersion: number;
  isLocked: boolean;
  profiles: OutputProfileRow[];
}

export interface SaveOutputProfilePayload {
  accountingPeriodId: string;
  profileCode: string;
  isEnabled: boolean;
  pages: Array<{ pageId: number; include: boolean }>;
}

export interface GenerateOutputProfilePayload {
  accountingPeriodId: string;
  profileCode: string;
  reportId: string | null;
  isPrint: boolean;
}

// ---------------------------------------------------------------- Validation centre

export interface ValidationOverrideInfo {
  reason: string;
  overriddenBy: string;
  overriddenAtUtc: string;
  isStale: boolean;
}

export interface ValidationCentreIssue {
  ruleCode: string;
  category: string;
  severity: string;
  severityId: number;
  message: string;
  detail: string | null;
  objectType: string | null;
  objectId: string | null;
  fixRoute: string | null;
  sourceReference: string | null;
  canOverride: boolean;
  override: ValidationOverrideInfo | null;
  /** Set on a Full, Abridged or Filleted eligibility issue; null for issues that cover the whole period. */
  outputProfile?: string | null;
}

export interface ValidationCentreScreen {
  accountingPeriodId: string;
  periodLabel: string;
  dataVersion: number;
  isLocked: boolean;
  lastRunAtUtc: string | null;
  isRunStale: boolean;
  blockingCount: number;
  warningCount: number;
  informationCount: number;
  overriddenCount: number;
  canFinalise: boolean;
  categories: string[];
  issues: ValidationCentreIssue[];
  /** The kind of accounts the list is scoped to; null when it covers the period. */
  outputProfile?: string | null;
}

export interface OverrideValidationIssuePayload {
  accountingPeriodId: string;
  ruleCode: string;
  objectId: string | null;
  scope: string | null;
  severity: number;
  /** Null clears the override. */
  reason: string | null;
}

// ---- ap5-a6 (item 134): entering statutory database answers ----
// The statutory screen only ever displayed answers and deep-linked elsewhere to set them. The
// disclosure endpoints below key answers by `fieldId` (a GUID); sending `fieldCode` is rejected
// with AP-DISC-002.

/** valueType: 0 Text, 1 Decimal, 2 Date, 3 Boolean, 4 Choice. */
export interface DisclosureChoiceOption {
  value: string;
  label: string;
  sortOrder: number;
}

export interface DisclosureFieldValue {
  disclosureValueId: string;
  valueText: string | null;
  valueDecimal: number | null;
  valueDate: string | null;
  valueBoolean: boolean | null;
  source: string;
  reviewStatus: number;
  updatedAtUtc: string;
  updatedBy: string;
}

export interface DisclosureFieldView {
  fieldId: string;
  fieldCode: string;
  label: string;
  helpText: string | null;
  valueType: number;
  isRequired: boolean;
  isApplicable: boolean;
  applicabilityRule: string | null;
  validationJson: string | null;
  choices: DisclosureChoiceOption[];
  value: DisclosureFieldValue | null;
  sortOrder: number;
}

export interface DisclosureSectionView {
  sectionCode: string;
  title: string;
  regulatoryPackVersionId: string;
  fields: DisclosureFieldView[];
  eTag: string;
}

export interface DisclosureAnswerInput {
  fieldId: string;
  valueType: number;
  valueText?: string | null;
  valueDecimal?: number | null;
  valueDate?: string | null;
  valueBoolean?: boolean | null;
  dimensionKey?: string | null;
}

export interface SaveDisclosureAnswersPayload {
  expectedETag: string | null;
  answers: DisclosureAnswerInput[];
}

export interface SaveDisclosureAnswersResponse {
  sectionCode: string;
  saved: number;
  cleared: number;
  eTag: string;
  savedAtUtc: string;
}
